/**
 * Exact (BigInt) package pricing for the wallet preview. Money is an
 * integer-Rial digit string end to end; nothing here touches `Number`.
 * Kept free of runtime imports so the unit test can load it directly.
 */

const BASIS_POINT_DENOMINATOR = 10_000n;

/** Converts English and Persian/Arabic digits to clean ASCII digits, stripping non-numeric chars. */
export function parseDigits(input: string): string {
  return input
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 1776))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 1632))
    .replace(/[^\d]/g, '');
}

/** Formats an integer digit string with Persian thousands separators (e.g. '۱۲٬۵۰۰٬۰۰۰'). */
export function formatThousandsPersian(digits: string): string {
  if (!digits) return '';
  return BigInt(digits).toLocaleString('fa-IR');
}

/** Validates a server/catalog or user top-up amount without lossy Number conversion. */
export function topupAmount(
  value: string,
  bounds: { readonly minToman: string; readonly maxToman?: string | null },
  options?: { readonly allowUnlimitedMax?: boolean },
): { readonly amountToman: string; readonly amountRial: string } | null {
  if (!/^\d+$/.test(value) || !/^\d+$/.test(bounds.minToman)) {
    return null;
  }
  const amount = BigInt(value);
  const min = BigInt(bounds.minToman);
  const max =
    options?.allowUnlimitedMax || !bounds.maxToman || bounds.maxToman === ''
      ? null
      : BigInt(bounds.maxToman);
  if (amount <= 0n || min <= 0n || amount < min || (max !== null && max >= min && amount > max)) {
    return null;
  }
  return { amountToman: amount.toString(), amountRial: (amount * 10n).toString() };
}

/** Exact display of a server-owned integer string, for token and amount values. */
export function formatIntegerString(value: string): string {
  return BigInt(value).toLocaleString('fa-IR');
}

/** VAT on a Rial amount, floored to a whole Rial: `amount × bp / 10 000`. */
export function vatRial(amountRial: string, basisPoints: number): string {
  return ((BigInt(amountRial) * BigInt(basisPoints)) / BASIS_POINT_DENOMINATOR).toString();
}

/** Amount plus VAT, as an integer-Rial digit string. */
export function totalWithVatRial(amountRial: string, basisPoints: number): string {
  return (BigInt(amountRial) + BigInt(vatRial(amountRial, basisPoints))).toString();
}

/**
 * Price of 1 000 tokens, floored to a whole Rial. Display aid only ("هر ۱۰۰۰
 * توکن: …"); the charged amount is always the package price itself.
 */
export function pricePerThousandTokensRial(priceRial: string, tokens: number): string {
  if (!Number.isInteger(tokens) || tokens <= 0) return '0';
  return ((BigInt(priceRial) * 1000n) / BigInt(tokens)).toString();
}

/** Whole-number percentage of `part` in `whole` (token counts), clamped to 0–100. */
export function percentOf(part: number, whole: number): number {
  if (!Number.isInteger(part) || !Number.isInteger(whole) || whole <= 0 || part <= 0) return 0;
  const pct = Number((BigInt(part) * 100n) / BigInt(whole));
  return Math.min(100, pct);
}

/** Exact integer sum of token deltas. */
export function sumTokens(values: readonly number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

/**
 * Tokens a purchase order issued. A package order carries its frozen quota (ADR-021). A legacy
 * order has none, so it is derived from the credit it issued: the wallet unit is one token per
 * `conversionRate / 5` micro-credits. The paid Toman amount is never multiplied: it is a price,
 * and since ADR-021 it no longer fixes the quota. Returns null when the order is not exact.
 */
export function orderTokenQuota(order: {
  readonly tokenQuota?: string | null;
  readonly amountMicroCredits: string;
  readonly conversionRate: string;
}): string | null {
  if (order.tokenQuota !== undefined && order.tokenQuota !== null) return order.tokenQuota;
  const peg = BigInt(order.conversionRate);
  const unit = peg / 5n;
  if (peg <= 0n || peg % 5n !== 0n) return null;
  const micro = BigInt(order.amountMicroCredits);
  return micro % unit === 0n ? (micro / unit).toString() : null;
}
